import React, { useState } from "react";
import { Box, Flex, Text, VStack, Badge, Separator } from "@chakra-ui/react";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import useAxios from "../../../hooks/useAxios";
import useToaster from "../../../hooks/useToaster";
import useThemeColors from "../../../hooks/useThemeColors";
import SectionHeader from "../../CustomUi/SectionHeader";
import useIcons from "../../../hooks/useIcons";
import Cad from "../../CustomUi/Cad";
import CustomButton from "../../CustomUi/CustomButton";
import CustomCheckBox from "../../CustomUi/CustomCheckBox";



const steps = [
  { label: "Select Classes" },
  { label: "Configure Each Class" },
  { label: "Select Format" },
  { label: "Review Selection" },
];

type SessionConfig = {
  sessionId: string;
  reports: string[]; // Holds term IDs and "cumulative"
};

type ClassConfig = {
  sessions: SessionConfig[];
};

const BulkReport: React.FC = () => {
  const { sessions, levels, terms, school, classes } = useSchoolsContext();
  const axiosClient = useAxios();
  const { showSuccessToast, showErrorToast } = useToaster();
  const colors = useThemeColors();
  const icons = useIcons();

  // State
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [classConfigs, setClassConfigs] = useState<{
    [classId: string]: ClassConfig;
  }>({});
  const [format, setFormat] = useState<"pdf" | "excel">("pdf");

  const [isLoading, setIsLoading] = useState(false);

  // Handle class selection
  const handleClassChange = (classId: string, checked: boolean) => {
    if (checked) {
      setSelectedClasses((prev) => [...prev, classId]);
      setClassConfigs((prev) => {
        const updated = { ...prev };
        if (!updated[classId]) {
          updated[classId] = {
            sessions: school?.currentSessionId
              ? [
                  {
                    sessionId: school.currentSessionId,
                    reports: school?.currentTermId
                      ? [school.currentTermId]
                      : [],
                  },
                ]
              : [],
          };
        }
        return updated;
      });
    } else {
      setSelectedClasses((prev) => prev.filter((id) => id !== classId));
      setClassConfigs((prev) => {
        const updated = { ...prev };
        delete updated[classId];
        return updated;
      });
    }
  };

  // Handle session selection
  const handleSessionChange = (
    classId: string,
    sessionId: string,
    checked: boolean
  ) => {
    setClassConfigs((prev) => {
      const prevSessions = prev[classId]?.sessions || [];
      let newSessions;

      if (checked) {
        newSessions = [
          ...prevSessions,
          {
            sessionId,
            reports: [],
          },
        ];
      } else {
        newSessions = prevSessions.filter((s) => s.sessionId !== sessionId);
      }

      return {
        ...prev,
        [classId]: { sessions: newSessions },
      };
    });
  };

  // Handle report selection
  const handleReportChange = (
    classId: string,
    sessionId: string,
    reportId: string,
    checked: boolean
  ) => {
    setClassConfigs((prev) => {
      const sessionsArr = prev[classId].sessions.map((s) => {
        if (s.sessionId === sessionId) {
          let reports = [...s.reports];
          if (checked) {
            reports.push(reportId);
          } else {
            reports = reports.filter((r) => r !== reportId);
          }
          return { ...s, reports };
        }
        return s;
      });

      return {
        ...prev,
        [classId]: { sessions: sessionsArr },
      };
    });
  };

  // Group classes by level
  const classesByLevel: { [levelId: string]: any[] } = {};
  classes.forEach((c) => {
    if (!classesByLevel[c.levelId]) classesByLevel[c.levelId] = [];
    classesByLevel[c.levelId].push(c);
  });

  // Generate summary
  const summary = selectedClasses.map((classId) => {
    const classObj = classes.find((c: any) => c.id === classId);
    const config = classConfigs[classId] || { sessions: [] };
    return {
      className: classObj?.className || classId,
      sessions: config.sessions.map((s) => ({
        sessionId: s.sessionId,
        reports: s.reports,
      })),
    };
  });

  // Validation
  const canGenerate =
    selectedClasses.length > 0 &&
    selectedClasses.every(
      (classId) =>
        (classConfigs[classId]?.sessions.length || 0) > 0 &&
        classConfigs[classId].sessions.every(
          (session) => (session.reports?.length || 0) > 0
        )
    );

  // Handle generate report
  const handleGenerate = async () => {
    setIsLoading(true);
    try {
      const payload = {
        requests: selectedClasses.flatMap((classId) => {
          const config = classConfigs[classId];
          if (!config || !config.sessions || config.sessions.length === 0) {
            return [];
          }

          return config.sessions.map((sessionConfig) => ({
            classId: classId,
            session: {
              sessionId: sessionConfig.sessionId,
              termIds: sessionConfig.reports.filter((r) => r !== "cumulative"),
              generateCumulativeReport:
                sessionConfig.reports.includes("cumulative"),
            },
          }));
        }),
      };

      const endpoint =
        format === "excel"
          ? "resultreports/generatebulkexcelreport"
          : "resultreports/generatebulkpdfreport";

      const response = await axiosClient.post(endpoint, payload, {
        responseType: "arraybuffer",
      });

      const blob = new Blob([response.data], {
        type:
          format.toLowerCase() === "pdf"
            ? "application/zip"
            : "application/xlsx",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bulk_Reports_${new Date().toISOString().slice(0, 10)}.${
        format.toLowerCase() === "pdf" ? "zip" : "xlsx"
      }`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      showSuccessToast("Bulk report generated and download started!");
    } catch (error: any) {
      let errorMessage = "Failed to generate report.";
      if (error.response && error.response.data instanceof ArrayBuffer) {
        try {
          const decoder = new TextDecoder("utf-8");
          const errorText = decoder.decode(error.response.data);
          // Try to parse as JSON if it's a structured error, otherwise use raw text
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.detail || errorText;
        } catch (decodeError) {
          // If decoding or JSON parsing fails, use the generic message or original error message
      
          errorMessage = "Failed to generate report: " + error.message; // Fallback to generic with JS error
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex flex={1} flexDirection={"column"} h={"100%"}>
      <Flex flex={1} flexDirection="column" h="100%">
        <VStack align="stretch" gap={0} position="relative">
          {steps.map((step, idx) => (
            <Flex
              key={step.label}
              align="flex-start"
              position="relative"
              minH="60px"
            >
              {/* Timeline/stepper left column */}
              <Box
                minW="40px"
                mr={2}
                display="flex"
                flexDirection="column"
                alignItems="center"
                position="relative"
                height="100%"
                mt={3}
              >
                {/* Step circle */}
                <Box
                  w="28px"
                  h="28px"
                  borderRadius="full"
                  bg={colors.background.step}
                  color={colors.text.secondary}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                  fontSize="md"
                  zIndex={1}
                >
                  {idx + 1}
                </Box>
                {/* Connector line */}
                {idx < steps.length - 1 && (
                  <Box
                    position="absolute"
                    top="28px"
                    left="50%"
                    transform="translateX(-50%)"
                    width="2px"
                    height="calc(100% - 28px)"
                    bg={colors.background.step}
                    zIndex={0}
                  />
                )}
              </Box>
              {/* Step content */}
              <Box
                flex={1}
                mb={idx < steps.length - 1 ? 6 : 0}
                position="relative"
              >
                {idx === 0 && (
                  <Cad>
                    <SectionHeader
                      title="Select Classes"
                      subtitle="Choose the classes you want to generate reports for"
                      icon={icons.main.class}
                    />
                    <Separator />
                    <VStack align="stretch" gap={3}>
                      {levels.map((level) => (
                        <Box key={level.id} mb={1}>
                          <Text fontWeight="semibold" opacity={0.6} mb={1}>
                            {level.levelName}
                          </Text>
                          <Flex direction="row" wrap="wrap" gap={8}>
                            {(classesByLevel[level.id] || []).map((c) => (
                              <CustomCheckBox
                                key={c.id}
                                checked={selectedClasses.includes(c.id)}
                                label={c.className}
                                onChange={() =>
                                  handleClassChange(
                                    c.id,
                                    !selectedClasses.includes(c.id)
                                  )
                                }
                              />
                            ))}
                          </Flex>
                          {level.id !== levels[levels.length - 1].id && (
                            <Box
                              borderTop="1px"
                              borderColor={colors.border.secondary}
                              mt={3}
                            />
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </Cad>
                )}
                {idx === 1 && (
                  <Cad>
                    <SectionHeader
                      title="Configure Classes"
                      subtitle="Choose the sessions and terms you want to generate reports for"
                      icon={icons.main.assessment}
                    />
                    <Separator />

                    {selectedClasses.map((classId) => {
                      const classObj = classes.find((c) => c.id === classId);
                      const levelObj = levels.find(
                        (l) => l.id === classObj?.levelId
                      );
                      const config = classConfigs[classId] || { sessions: [] };

                      return (
                        <Box
                          key={classId}
                          borderWidth="1px"
                          borderRadius="lg"
                          p={4}
                          bg={colors.background.secondary}
                          mb={4}
                        >
                          <Flex align="center" mb={3} gap={2}>
                            <Badge colorPalette="gray">
                              {levelObj?.levelName}
                            </Badge>
                            <Text fontWeight="semibold" fontSize="md">
                              {classObj?.className}
                            </Text>
                          </Flex>
                          {/* Session selection */}
                          <Box mb={3}>
                            <Text fontWeight="semibold" mb={1} opacity={0.6}>
                              Sessions
                            </Text>
                            <Flex direction="row" wrap="wrap" gap={8}>
                              {sessions.map((s) => (
                                <CustomCheckBox
                                  key={s.id}
                                  checked={config.sessions.some(
                                    (session) => session.sessionId === s.id
                                  )}
                                  label={s.sessionName}
                                  onChange={() =>
                                    handleSessionChange(
                                      classId,
                                      s.id,
                                      !config.sessions.some(
                                        (session) => session.sessionId === s.id
                                      )
                                    )
                                  }
                                />
                              ))}
                            </Flex>
                          </Box>
                          {/* Per-session term/report selection */}
                          {config.sessions.map((sessionConfig) => (
                            <Box
                              key={sessionConfig.sessionId}
                              mt={2}
                              mb={2}
                              p={3}
                              borderWidth="1px"
                              borderRadius="md"
                              bg={colors.background.tertiary}
                            >
                              <Text fontWeight="medium" mb={1}>
                                {
                                  sessions.find(
                                    (s) => s.id === sessionConfig.sessionId
                                  )?.sessionName
                                }
                              </Text>
                              <Flex direction="row" wrap="wrap" gap={8}>
                                {terms.map((t) => (
                                  <CustomCheckBox
                                    key={t.id}
                                    checked={sessionConfig.reports.includes(
                                      t.id
                                    )}
                                    label={t.termName}
                                    onChange={() =>
                                      handleReportChange(
                                        classId,
                                        sessionConfig.sessionId,
                                        t.id,
                                        !sessionConfig.reports.includes(t.id)
                                      )
                                    }
                                  />
                                ))}
                                <CustomCheckBox
                                  checked={sessionConfig.reports.includes(
                                    "cumulative"
                                  )}
                                  label="Cumulative Result"
                                  onChange={() =>
                                    handleReportChange(
                                      classId,
                                      sessionConfig.sessionId,
                                      "cumulative",
                                      !sessionConfig.reports.includes(
                                        "cumulative"
                                      )
                                    )
                                  }
                                />
                              </Flex>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Cad>
                )}
                {idx === 2 && (
                  <Cad>
                    <SectionHeader
                      title="Select Format"
                      subtitle="Choose the format you want to generate reports in"
                      icon={icons.utility.download}
                    />
                    <Separator />
                    {selectedClasses.length > 0 && (
                      <Flex direction="row" gap={8}>
                        <CustomCheckBox
                          checked={format === "pdf"}
                          label="PDF"
                          onChange={() => setFormat("pdf")}
                        />
                        <CustomCheckBox
                          checked={format === "excel"}
                          label="Excel"
                          onChange={() => setFormat("excel")}
                        />
                      </Flex>
                    )}
                  </Cad>
                )}
                {idx === 3 && (
                  <Cad>
                    <SectionHeader
                      title="Review Selection"
                      subtitle="Review the classes and sessions you have selected"
                      icon={icons.utility.review}
                    />
                    <VStack align="start" gap={2}>
                      {summary.map((s) => (
                        <Box
                          key={s.className}
                          bg={colors.background.secondary}
                          borderRadius="md"
                          boxShadow="xs"
                          px={3}
                          py={2}
                          w="100%"
                        >
                          <Text fontWeight="semibold" mb={1}>
                            {s.className}
                          </Text>
                          {s.sessions.map((session) => (
                            <Box key={session.sessionId} ml={4} mt={1} my={5}>
                              <Flex align="" gap={2} mb={1}>
                                <Badge
                                  colorPalette={colors.schoolAccent.primary}
                                  variant="solid"
                                  px={2}
                                  py={1}
                                >
                                  {sessions.find(
                                    (sess) => sess.id === session.sessionId
                                  )?.sessionName || session.sessionId}
                                </Badge>

                                <Flex direction="row" gap={2} flexWrap="wrap">
                                  {session.reports.map((reportType) => (
                                    <Badge
                                      key={reportType}
                                      // Set cumulative badge color to red, others purple
                                      colorPalette="gray"
                                      variant={
                                        reportType === "cumulative"
                                          ? "outline"
                                          : "subtle"
                                      }
                                      px={2}
                                      py={1}
                                    >
                                      {reportType === "cumulative"
                                        ? "Cumulative"
                                        : terms.find(
                                            (term) => term.id === reportType
                                          )?.termName || reportType}
                                    </Badge>
                                  ))}
                                  <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="gray.600"
                                  >
                                    Reports
                                  </Text>
                                </Flex>
                              </Flex>
                            </Box>
                          ))}
                          <Box
                            borderTop="1px"
                            borderColor={colors.border.secondary}
                            mt={3}
                          />
                        </Box>
                      ))}
                    </VStack>
                    <CustomButton
                      icon={icons.utility.download}
                      label="Generate Report"
                      colorPalette={colors.schoolAccent.primary}
                      onClick={handleGenerate}
                      disabled={!canGenerate || isLoading}
                      loading={isLoading}
                      alignSelf="flex-end"
                      mt={6}
                    />
                  </Cad>
                )}
              </Box>
            </Flex>
          ))}
        </VStack>
      </Flex>
    </Flex>
  );
};

export default BulkReport;
